import { MgmtApi } from '@/customApiClients/mgmtApiClient/Api';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useRecoilState } from 'recoil';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { SettingRow } from '@/components/ui/setting-row';
import { Switch } from '@/components/ui/switch';

import { debugModeState } from 'state/chat';

import { SessionBudgetTab } from './SessionBudgetTab';

const AVAILABLE_FONTS = [
    'Arial',
    'Garamond',
    'Georgia',
    'Helvetica',
    'Inter',
    'Lato',
    'Montserrat',
    'Nunito',
    'Open Sans',
    'Poppins',
    'PT Sans',
    'Raleway',
    'Roboto',
    'Tahoma',
    'Times New Roman',
    'Trebuchet MS',
    'Verdana'
];

const FONT_SIZES = [
    { value: '12px', label: 'Small (12px)' },
    { value: '14px', label: 'Medium (14px)' },
    { value: '16px', label: 'Large (16px)' },
    { value: '18px', label: 'Extra Large (18px)' }
];

interface Props {
    mgmtApi: MgmtApi<unknown>;
}

export const SettingsTab: React.FC<Props> = ({ mgmtApi }) => {
    const [debugMode, setDebugMode] = useRecoilState(debugModeState);

    // Colors
    const [primaryColor, setPrimaryColor] = useState('#34648A');
    const [secondaryColor, setSecondaryColor] = useState('#619EC6');
    const [accentColor, setAccentColor] = useState('#D37C32');
    const [backgroundColor, setBackgroundColor] = useState('#f0e4d7');
    const [textColor, setTextColor] = useState('#0a1e2f');
    const [colorsSaving, setColorsSaving] = useState(false);

    // Fonts
    const [primaryFont, setPrimaryFont] = useState('Inter');
    const [fontSize, setFontSize] = useState('14px');
    const [fontsSaving, setFontsSaving] = useState(false);


    useEffect(() => {
        mgmtApi.api
            .v1OrganizationsColorsList()
            .then((res) => {
                const colors = res?.data?.data?.[0];
                if (colors) {
                    setPrimaryColor(colors.color1 || '#34648A');
                    setSecondaryColor(colors.color2 || '#619EC6');
                    setAccentColor(colors.color3 || '#D37C32');
                    setBackgroundColor(colors.color4 || '#f0e4d7');
                    setTextColor(colors.color5 || '#0a1e2f');
                }
            })
            .catch((e) =>
                console.error('Failed to load organization colors:', e)
            );
    }, [mgmtApi]);

    useEffect(() => {
        mgmtApi.api
            .v1OrganizationsFontsList()
            .then((res) => {
                const fonts = res?.data?.data?.[0];
                if (fonts) {
                    setPrimaryFont(fonts.font || 'Inter');
                    setFontSize(fonts.fontSize || '14px');
                }
            })
            .catch((e) =>
                console.error('Failed to load organization fonts:', e)
            );
    }, [mgmtApi]);

    const handleSaveColors = async () => {
        setColorsSaving(true);
        try {
            await mgmtApi.api.v1OrganizationsColorsCreate({
                color1: primaryColor,
                color2: secondaryColor,
                color3: accentColor,
                color4: backgroundColor,
                color5: textColor
            });
            toast.success('Colors saved');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to save colors');
        } finally {
            setColorsSaving(false);
        }
    };

    const handleSaveFonts = async () => {
        setFontsSaving(true);
        try {
            await mgmtApi.api.v1OrganizationsFontsCreate({
                font: primaryFont,
                fontSize: fontSize
            });
            localStorage.setItem('org-font', primaryFont);
            localStorage.setItem('org-font-size', fontSize);
            document.documentElement.style.setProperty(
                '--font-sans',
                `'${primaryFont}', sans-serif`
            );
            document.documentElement.style.fontSize = fontSize;

            const googleFonts = [
                'Inter',
                'Roboto',
                'Open Sans',
                'Lato',
                'Montserrat',
                'Poppins',
                'Nunito',
                'PT Sans',
                'Raleway'
            ];
            if (googleFonts.includes(primaryFont)) {
                const existingLink = document.querySelector(
                    `link[href*="${primaryFont.replace(/\s/g, '+')}"]`
                );
                if (!existingLink) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = `https://fonts.googleapis.com/css2?family=${primaryFont.replace(
                        /\s/g,
                        '+'
                    )}:wght@300;400;500;600;700&display=swap`;
                    document.head.appendChild(link);
                }
            }
            toast.success('Fonts saved');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to save fonts');
        } finally {
            setFontsSaving(false);
        }
    };

    const colorFields = [
        { label: 'First', value: primaryColor, setter: setPrimaryColor },
        { label: 'Second', value: secondaryColor, setter: setSecondaryColor },
        { label: 'Third', value: accentColor, setter: setAccentColor },
        { label: 'Fourth', value: backgroundColor, setter: setBackgroundColor },
        { label: 'Fifth', value: textColor, setter: setTextColor }
    ];

    return (
        <div className="space-y-10">
            <section className="space-y-4">
                <SectionHeader
                    title="General"
                    description="Settings that apply across the chat interface"
                />
                <SettingRow
                    label="Debug mode"
                    description="Show additional debugging information in the chat interface"
                    htmlFor="debug-mode"
                    wholeRowClickable
                    className="border-b-0"
                >
                    <Switch
                        id="debug-mode"
                        checked={debugMode}
                        onCheckedChange={setDebugMode}
                    />
                </SettingRow>
            </section>

            <section className="space-y-4">
                <SectionHeader
                    title="Color theme"
                    level="section"
                    description="Customize the color scheme for your organization"
                />
                <div className="gap-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                    {colorFields.map(({ label, value, setter }) => (
                        <div key={label} className="space-y-2">
                            <Label htmlFor={`color-${label}`}>{label}</Label>
                            <input
                                type="color"
                                value={value}
                                onChange={(e) => setter(e.target.value)}
                                aria-label={`${label} color`}
                                className="bg-transparent p-1 border border-input rounded-lg w-full h-10 cursor-pointer"
                            />
                            <Input
                                id={`color-${label}`}
                                value={value}
                                onChange={(e) => setter(e.target.value)}
                            />
                        </div>
                    ))}
                </div>
                <Button onClick={handleSaveColors} disabled={colorsSaving}>
                    {colorsSaving ? (
                        <>
                            <Loader2 className="mr-2 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        'Save colors'
                    )}
                </Button>
            </section>

            <section className="space-y-4">
                <SectionHeader
                    title="Typography"
                    level="section"
                    description="Customize the font settings for your organization"
                />
                <div>
                    <SettingRow
                        label="Font family"
                        htmlFor="font-family"
                        description="Used across the whole application"
                    >
                        <Select value={primaryFont} onValueChange={setPrimaryFont}>
                            <SelectTrigger
                                id="font-family"
                                className="w-56"
                                style={{ fontFamily: primaryFont }}
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {AVAILABLE_FONTS.map((font) => (
                                    <SelectItem
                                        key={font}
                                        value={font}
                                        style={{ fontFamily: font }}
                                    >
                                        {font}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </SettingRow>
                    <SettingRow
                        label="Font size"
                        htmlFor="font-size"
                        description="Base size the interface scales from"
                    >
                        <Select value={fontSize} onValueChange={setFontSize}>
                            <SelectTrigger id="font-size" className="w-56">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {FONT_SIZES.map((size) => (
                                    <SelectItem
                                        key={size.value}
                                        value={size.value}
                                    >
                                        {size.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </SettingRow>
                </div>
                <div
                    className="flex justify-center items-center bg-card px-4 py-12 border border-border rounded-lg font-medium"
                    style={{ fontFamily: primaryFont, fontSize }}
                >
                    The quick brown fox jumps over the lazy dog - 1234567890
                </div>
                <Button onClick={handleSaveFonts} disabled={fontsSaving}>
                    {fontsSaving ? (
                        <>
                            <Loader2 className="mr-2 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        'Save fonts'
                    )}
                </Button>
            </section>

            <section className="space-y-4">
                <SectionHeader
                    title="Session budget"
                    level="section"
                    description="Limit how long managed-agent chats run before suggesting a new chat"
                />
                <SessionBudgetTab />
            </section>
        </div>
    );
};
